import axios, { type AxiosInstance } from 'axios'
import { useAuthStore } from './store'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: `${BASE_URL}/api/v1`,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30_000,
  })

  // Attach JWT from Zustand store (non-reactive — reads current state). On a
  // fresh page load (e.g. right after the post-login hard navigation) the
  // persisted store may not have rehydrated into memory yet, so fall back to
  // reading the token straight out of the persisted 'scion-auth' localStorage
  // entry. Without this fallback, queries that fire during the rehydration
  // window go out with no Authorization header → 401 → the response interceptor
  // clears auth and bounces back to /login (the "brief dashboard then redirect"
  // symptom).
  client.interceptors.request.use((config) => {
    let token = useAuthStore.getState().token
    if (!token) {
      try {
        const raw = localStorage.getItem('scion-auth')
        if (raw) token = JSON.parse(raw)?.state?.token ?? null
      } catch { /* ignore malformed storage */ }
    }
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  // On 401: try ONE token refresh (single-flight, shared across all concurrent
  // 401s) and retry the original request. Only clear auth + bounce to /login if
  // the refresh itself fails. This prevents the cascade where the first failing
  // request among many parallel dashboard fetches calls clearAuth() — which
  // (via the persist middleware) writes token:null to localStorage — leaving
  // every other in-flight request to go out tokenless and 401 with "missing
  // header", then redirect to /login (the "brief dashboard then bounce" bug).
  client.interceptors.response.use(
    (r) => r,
    async (error) => {
      const original = error.config
      const status = error.response?.status
      // Don't try to refresh the refresh call itself, or retry forever.
      const isRefreshCall = original?.url?.includes('/auth/refresh')
      if (status !== 401 || isRefreshCall || original?._retried) {
        if (status === 401 && (isRefreshCall || original?._retried)) {
          hardLogout()
        }
        return Promise.reject(error)
      }

      original._retried = true
      try {
        const newToken = await refreshAccessToken()
        if (!newToken) {
          hardLogout()
          return Promise.reject(error)
        }
        original.headers = original.headers ?? {}
        original.headers.Authorization = `Bearer ${newToken}`
        return client(original)
      } catch {
        hardLogout()
        return Promise.reject(error)
      }
    }
  )

  return client
}

// Single-flight refresh: concurrent 401s share one in-flight refresh promise so
// we don't fire N parallel /auth/refresh calls (which would rotate the refresh
// token N times and invalidate each other).
let refreshInFlight: Promise<string | null> | null = null
function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const refresh = localStorage.getItem('refresh_token')
      if (!refresh) return null
      const res = await axios.post(`${BASE_URL}/api/v1/auth/refresh`, { refresh_token: refresh })
      const token = res.data?.access_token ?? null
      if (token) {
        const st = useAuthStore.getState()
        st.setAuth(token, st.tenant, res.data?.role ?? st.role, res.data?.status ?? st.status ?? 'active')
        if (res.data?.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token)
      }
      return token
    } catch {
      return null
    } finally {
      // Allow a subsequent independent refresh later.
      setTimeout(() => { refreshInFlight = null }, 0)
    }
  })()
  return refreshInFlight
}

// hardLogout clears auth and returns to /login — only when refresh truly fails.
// Guarded so concurrent failures don't each kick off a navigation.
let loggingOut = false
// Public/unauthenticated routes: if an authed request 401s while we're already on
// one of these, DON'T do a full-page reload to /login — that reload remounts the
// page, which re-fires the request, which 401s again → infinite reload loop. Just
// clear auth in place. (Defense-in-depth behind the query gating in the pages
// themselves; the growable-ref loop was one instance of this.)
const PUBLIC_PATH_PREFIXES = [
  '/login', '/signup', '/get-started', '/reset-password', '/accept-invite',
  '/join', '/pending', '/terms', '/privacy', '/unsubscribe',
]
function onPublicPath(): boolean {
  const p = window.location.pathname
  return PUBLIC_PATH_PREFIXES.some((pre) => p === pre || p.startsWith(pre + '/'))
}
function hardLogout() {
  if (loggingOut) return
  loggingOut = true
  useAuthStore.getState().clearAuth()
  localStorage.removeItem('refresh_token')
  // Already on a public page → clear auth without navigating (prevents the
  // 401 → reload → refire → 401 loop). Otherwise send them to /login.
  if (onPublicPath()) return
  window.location.href = '/login'
}

export const api = createClient()

// authToken returns the current JWT for code paths that use raw fetch instead of
// the axios client (e.g. accessibility prefs, billing). Prefers the in-memory
// store, falling back to the persisted 'scion-auth' entry during rehydration.
// NOTE: the auth token is persisted under the 'scion-auth' Zustand key, NOT a
// bare 'token' localStorage key — reading localStorage.getItem('token') returns
// null and causes spurious 401s.
export function authToken(): string | null {
  const t = useAuthStore.getState().token
  if (t) return t
  try {
    const raw = localStorage.getItem('scion-auth')
    return raw ? (JSON.parse(raw)?.state?.token ?? null) : null
  } catch {
    return null
  }
}

// ── Docs ─────────────────────────────────────────────────────
export interface DocNavItem {
  slug: string
  title: string
  section: string
  sort_order: number
  parent_slug?: string
  children?: DocNavItem[]
}
export interface DocPage {
  slug: string
  section: string
  title: string
  content: string
  excerpt?: string
  last_updated_at?: string
}
export const docsApi = {
  nav: () => api.get<DocNavItem[]>('/docs/nav'),
  page: (slug: string) => api.get<DocPage>('/docs/page', { params: { slug } }),
  search: (q: string) =>
    api.get<{ slug: string; title: string; section: string; excerpt: string }[]>('/docs/search', { params: { q } }),
}

// ── Auth ─────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ token: string; tenant: Tenant }>('/auth/login', { email, password }),
  register: (data: RegisterPayload) =>
    api.post<{ token: string; tenant: Tenant }>('/auth/register', data),
  refresh: () => api.post<{ access_token: string }>('/auth/refresh', { refresh_token: localStorage.getItem('refresh_token') }),
  // Revoke the refresh token server-side on sign-out (§208) so it can't mint new
  // access tokens afterward. Fire-and-forget — the UI signs out regardless.
  logout: () => api.post<void>('/auth/logout', { refresh_token: localStorage.getItem('refresh_token') }),
  // Which social-login providers are configured (env creds present). Public. The
  // login/join pages hide a button for a provider that isn't set up yet (e.g. Apple
  // until the Apple Developer Program membership is active); it auto-reappears once
  // its credentials are configured — no flag to flip back on.
  providers: () => api.get<{ providers: string[]; google?: boolean; apple?: boolean }>('/auth/providers'),
}

// ── Care ─────────────────────────────────────────────────────
export const careApi = {
  upcoming: (days = 14) => api.get<CareEvent[]>('/care/upcoming', { params: { days } }),
  overdue:  ()          => api.get<CareEvent[]>('/care/overdue'),
  events:   (params?: { plant_id?: string; limit?: number }) =>
    api.get<{ events: CareEvent[] }>('/care/events', { params }),
  recentActivity: (limit = 20) =>
    api.get<{ events: CareEvent[] }>('/care/events', { params: { limit, sort: 'desc' } }),
}

// ── Calendar (.ics) subscription — care, tasks, harvest as a subscribable feed ──
export interface CalendarSubscription { url: string; token: string; webcal: string }
export const calendarApi = {
  subscription: () => api.get<CalendarSubscription>('/calendar/subscription'),
}

// ── Web push (free staff notifications, §225) ────────────────────────────────
export const pushApi = {
  vapidKey: () => api.get<{ public_key: string; configured: boolean }>('/push/vapid-key'),
  subscribe: (sub: PushSubscriptionJSON) => api.post<{ ok: boolean }>('/push/subscribe', sub),
  unsubscribe: (endpoint: string) => api.post<{ ok: boolean }>('/push/unsubscribe', { endpoint }),
}

// ── Social marketing posts (§227) ────────────────────────────────────────────
export type SocialPlatform = 'facebook' | 'instagram' | 'other' | 'general'
export interface SocialPost {
  id: string
  platform: SocialPlatform
  body: string
  image_url?: string
  status: 'draft' | 'posted' | 'scheduled'
  source: 'manual' | 'ai'
  external_id?: string
  posted_at?: string
  created_at: string
}
// ── Drone map overlays (§228) ────────────────────────────────────────────────
export interface DroneMap {
  id: string
  location_id?: string
  name: string
  captured_on?: string
  south?: number
  west?: number
  north?: number
  east?: number
  opacity: number
  is_active: boolean
  created_at: string
}
export const droneMapsApi = {
  list: () => api.get<{ maps: DroneMap[] }>('/drone-maps'),
  upload: (form: FormData) => api.post<DroneMap>('/drone-maps', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, data: Partial<DroneMap>) => api.put<DroneMap>(`/drone-maps/${id}`, data),
  remove: (id: string) => api.delete<void>(`/drone-maps/${id}`),
  // Authenticated image proxy → blob (imageOverlay can't carry the JWT on an <img src>).
  file: (id: string) => api.get<Blob>(`/drone-maps/${id}/file`, { responseType: 'blob' }),
}

export const socialApi = {
  list: () => api.get<{ posts: SocialPost[] }>('/social'),
  create: (data: Partial<SocialPost>) => api.post<SocialPost>('/social', data),
  update: (id: string, data: Partial<SocialPost>) => api.put<SocialPost>(`/social/${id}`, data),
  remove: (id: string) => api.delete<void>(`/social/${id}`),
  markPosted: (id: string) => api.post<{ ok: boolean }>(`/social/${id}/posted`, {}),
  // AI draft — 402 off-plan / 429 over the ai_marketing_copy quota.
  draft: (topic: string, platform: SocialPlatform) => api.post<{ draft: string; source: string }>('/social/draft', { topic, platform }),
}

// ── Picking labor (piece-rate pay, wage compliance, crew recs) ──────────────
export interface LaborPickerDay {
  picker_id: string; picker_name: string; hours: number; lbs: number; bins: number
  piece_pay_usd: number; topup_usd: number; ot_premium_usd: number; rest_break_usd: number
  total_pay_usd: number; effective_hourly_usd: number; lbs_per_hour: number; below_minimum: boolean
}
export interface LaborDaySummary {
  work_date: string; total_lb: number; total_bins: number; piece_pay_usd: number; topup_usd: number
  ot_premium_usd: number; rest_break_usd: number; total_labor_usd: number; picker_count: number
  total_hours: number; cost_per_lb_usd: number; avg_lb_per_hour: number; pickers: LaborPickerDay[]
  narrative: string; narrative_source: string; audio_url?: string; audio_status: string; disclaimer: string
}
export interface LaborPickerWeek {
  picker_id: string; picker_name: string; hours: number; daily_ot_hours: number
  weekly_ot_hours: number; regular_rate_usd: number; weekly_ot_premium_usd: number
}
export interface LaborWeekSummary {
  week_start: string; week_end: string; ot_threshold_hours: number; has_daily_ot: boolean
  pickers: LaborPickerWeek[]; total_hours: number; total_weekly_ot_hours: number
  total_weekly_ot_premium_usd: number; disclaimer: string
}
export interface LaborPickerStat {
  picker_id: string; picker_name: string; days_worked: number; hours: number; lbs: number
  lbs_per_hour: number; tier: string
}
export interface LaborPairing { mentor_id: string; mentor_name: string; mentee_id: string; mentee_name: string; reason: string }
export interface LaborRecommendation {
  window_days: number; picker_stats: LaborPickerStat[]; pairings: LaborPairing[]
  narrative: string; source: string; disclaimer: string
}
export interface LaborSettings {
  min_wage_usd: number; state_code: string; default_pay_unit: string; default_piece_rate_usd: number
  piece_rate_rest_breaks: boolean; ot_daily_hours?: number | null
}
export interface LaborRate { id: string; variety_id?: string | null; crop_label?: string | null; pay_unit: string; rate_usd: number; bin_target_lb?: number | null; is_active: boolean }
export interface LaborShift { id: string; picker_id: string; picker_name?: string; work_date: string; clock_in_at?: string; clock_out_at?: string; status: string; assigned_label?: string | null }

export const laborApi = {
  summary:         (date?: string) => api.get<LaborDaySummary>('/labor/summary', { params: date ? { date } : {} }),
  weekSummary:     (weekStart?: string) => api.get<LaborWeekSummary>('/labor/week-summary', { params: weekStart ? { week_start: weekStart } : {} }),
  recommendations: (windowDays?: number) => api.get<LaborRecommendation>('/labor/recommendations', { params: windowDays ? { window_days: windowDays } : {} }),
  shifts:          (date?: string) => api.get<{ shifts: LaborShift[] }>('/labor/shifts', { params: date ? { date } : {} }),
  clockIn:         (picker_id: string, zone_label?: string) => api.post<LaborShift>('/labor/shifts/clock-in', { picker_id, zone_label }),
  clockOut:        (id: string, unpaid_break_minutes = 0) => api.post<LaborShift>(`/labor/shifts/${id}/clock-out`, { unpaid_break_minutes }),
  addTally:        (body: { picker_id: string; pay_unit: string; quantity: number; weight_lb?: number; variety_id?: string }) => api.post('/labor/tallies', body),
  getSettings:     () => api.get<LaborSettings>('/labor/settings'),
  updateSettings:  (body: Partial<LaborSettings>) => api.put<LaborSettings>('/labor/settings', body),
  applyPreset:     (state: string) => api.post<LaborSettings>('/labor/settings/preset', { state }),
  listRates:       () => api.get<{ rates: LaborRate[] }>('/labor/rates'),
  createRate:      (body: { pay_unit: string; rate_usd: number; crop_label?: string; bin_target_lb?: number; variety_id?: string }) => api.post<LaborRate>('/labor/rates', body),
  deleteRate:      (id: string) => api.delete(`/labor/rates/${id}`),
  requestAudio:    (date?: string) => api.post<{ audio_status: string }>('/labor/summary/audio', null, { params: date ? { date } : {} }),
}

export const pickersApi = {
  list:   () => api.get<{ pickers: { id: string; name: string; is_active: boolean }[] }>('/picking/pickers'),
  create: (name: string) => api.post<{ id: string; name: string }>('/picking/pickers', { name }),
}

// ── Activity: narrative / spreadsheet → multiple events ─────────────────────
export interface ParsedEvent {
  action_type: string
  occurred_at: string        // RFC3339
  description: string
  resource_label?: string
  quantity?: number | null
  unit?: string
  product?: string
  confidence: number
  raw?: string
}
export const activityApi = {
  list:   (params?: Record<string, unknown>) => api.get('/activity', { params }),
  // parse = preview (no persistence); reference_date anchors relative dates ("today", "last Tuesday")
  parse:  (text: string, reference_date?: string) =>
            api.post<{ events: ParsedEvent[]; ai_used: boolean; warnings: string[] }>('/activity/parse', { text, reference_date }),
  commit: (events: ParsedEvent[]) => api.post<{ created: number; ids: string[] }>('/activity/commit', { events }),
}

// ── Blocks (orchard harvest-management units, above plants) ─────────────────
export interface Block {
  id: string; name: string; variety_id?: string | null; variety_name?: string | null
  rootstock?: string | null; planted_year?: number | null; acreage?: number | null
  tracking_mode: 'aggregate' | 'individual'; plant_count: number; effective_plant_count: number
  harvest_method: 'hand' | 'machine' | 'mixed'; harvest_window?: 'early' | 'mid' | 'late' | null
  zone_id?: string | null; location_id?: string | null; notes?: string | null
  purpose?: string // overrides variety default; inherited by member plants. '' = inherit
  category?: 'production' | 'support' | 'mixed' // ecosystem-support (mulch/shade/windbreak/N-fixer) vs producing
  trellis_type?: string | null; row_spacing_m?: number | null; vine_spacing_m?: number | null // vineyard mode (§55.20)
  is_active: boolean; created_at: string
  // Auto-derived from member plants (individual mode); a block may be mixed-variety.
  composition?: VarietyTag[]
}
export interface VarietyTag {
  variety_id?: string | null; variety_name: string; category?: string; plant_count: number
}
export interface BlockStats {
  block_id: string; effective_plant_count: number; harvest_kg_all_time: number
  harvest_kg_ytd: number; harvest_kg_hand: number; harvest_kg_machine: number; last_harvest_date?: string | null
}
export interface DailyPicking { plant_id?: string | null; plant_label: string; weight_kg: number; events: number }
export interface DailyPickingsReport {
  block_id: string; date: string; total_kg: number; events: number; plants_picked: number; by_plant: DailyPicking[]
}
export const blocksApi = {
  list:   () => api.get<{ blocks: Block[] }>('/blocks'),
  get:    (id: string) => api.get<Block>(`/blocks/${id}`),
  stats:  (id: string) => api.get<BlockStats>(`/blocks/${id}/stats`),
  // Roll-up of today's plant-level pickings linked to this block (block-QR "roll up").
  dailyPickings: (id: string, date?: string) =>
    api.get<DailyPickingsReport>(`/blocks/${id}/daily-pickings`, { params: { date } }),
  create: (b: Partial<Block>) => api.post<Block>('/blocks', b),
  update: (id: string, b: Partial<Block>) => api.put<Block>(`/blocks/${id}`, b),
  remove: (id: string) => api.delete(`/blocks/${id}`),
}

// ── Harvest cohorts + picking rounds (Blocks Phase 2, §94) ──────────────────────
export type HarvestWindow = 'early' | 'mid' | 'late'
export interface Cohort {
  id: string; name: string; season: string; variety_id?: string; variety_name?: string
  harvest_window: HarvestWindow; status: 'active' | 'closed'; notes: string
  block_ids: string[]; block_count: number; round_count: number
  percent_picked: number; harvested_kg: number; created_at: string
}
export interface PickingRound {
  id: string; cohort_id: string; round_number: number
  scheduled_date?: string; completed_date?: string
  percent_picked: number; weight_kg?: number; status: 'planned' | 'in_progress' | 'done'
  notes: string; created_at: string
}
export const cohortsApi = {
  list: () => api.get<{ cohorts: Cohort[] }>('/cohorts'),
  create: (c: Partial<Cohort>) => api.post<Cohort>('/cohorts', c),
  update: (id: string, c: Partial<Cohort>) => api.put<Cohort>(`/cohorts/${id}`, c),
  remove: (id: string) => api.delete(`/cohorts/${id}`),
  rounds: (cohortId: string) => api.get<{ rounds: PickingRound[] }>(`/cohorts/${cohortId}/rounds`),
  addRound: (cohortId: string, r: Partial<PickingRound>) => api.post<PickingRound>(`/cohorts/${cohortId}/rounds`, r),
  updateRound: (id: string, r: Partial<PickingRound>) => api.put<PickingRound>(`/picking-rounds/${id}`, r),
  removeRound: (id: string) => api.delete(`/picking-rounds/${id}`),
  // Phase 3 — GDD readiness + pest timing.
  gdd: (cohortId: string) => api.get<CohortGDD>(`/cohorts/${cohortId}/gdd`),
  setGdd: (cohortId: string, body: Partial<CohortGDDInput>) => api.put<CohortGDD>(`/cohorts/${cohortId}/gdd`, body),
  cropBases: () => api.get<{ crop_bases: CropBase[] }>('/gdd/crop-bases'),
  pestModels: () => api.get<{ pest_models: PestModel[] }>('/gdd/pest-models'),
  createPestModel: (m: Partial<PestModel>) => api.post<PestModel>('/gdd/pest-models', m),
  removePestModel: (id: string) => api.delete(`/gdd/pest-models/${id}`),
  pestWatches: (cohortId: string) => api.get<{ watches: PestWatch[] }>(`/cohorts/${cohortId}/pest-watches`),
  addPestWatch: (cohortId: string, body: { pest_model_id: string; biofix_date: string }) =>
    api.post<{ watches: PestWatch[] }>(`/cohorts/${cohortId}/pest-watches`, body),
  removePestWatch: (id: string) => api.delete(`/pest-watches/${id}`),
}
export interface CohortGDD {
  configured: boolean; crop: string; biofix_date?: string; base_temp_f: number
  target_gdd?: number; gdd_offset: number; accumulated_gdd: number; daily_rate: number
  has_weather: boolean; days_to_target?: number; predicted_ready?: string
}
export interface CohortGDDInput { crop: string; biofix_date: string; base_temp_f: number; target_gdd?: number | null; gdd_offset: number }
export interface CropBase { crop: string; base_temp_f: number; note: string }
export interface PestStage { name: string; gdd: number; action: string }
export interface PestModel {
  id: string; global: boolean; crop: string; pest_name: string; base_temp_f: number
  biofix_event: string; description: string; stages: PestStage[]; source: string
  confidence: 'high' | 'medium' | 'phenology'
}
export interface PestWatch {
  id: string; pest_model_id: string; pest_name: string; biofix_date: string; base_temp_f: number
  accumulated_gdd: number; has_weather: boolean; confidence: string; source: string
  stages: PestStage[]; current_stage?: PestStage; next_stage?: PestStage
}

// ── Plant zones (map polygons) — a block's own geometry, or a species sub-zone
// inside a block (block_id set). The blocks-first map model creates these as the
// backing geometry/care/sun record for each block. ─────────────────────────────
export interface PlantZone {
  id: string; name: string; description?: string
  geometry?: GeoJSON                 // GeoJSON polygon
  color_hex?: string; growable_category?: string; common_name?: string
  sun_class?: string; plant_count?: number
  block_id?: string | null           // parent block when this is a sub-zone
  location_id?: string | null        // owning farm plot (per-plot map scoping)
  primary_variety_id?: string | null
}
export const plantZonesApi = {
  list:   () => api.get<PlantZone[]>('/plant-zones'),
  create: (z: Partial<PlantZone>) => api.post<PlantZone>('/plant-zones', z),
  update: (id: string, z: Partial<PlantZone>) => api.put<PlantZone>(`/plant-zones/${id}`, z),
  remove: (id: string) => api.delete(`/plant-zones/${id}`),
}

// ── Farm locations (multi-site; blocks/plants link via location_id) ─────────
export interface Location {
  id: string; name: string; address: string; city: string; state: string; zip: string
  lat?: number | null; lon?: number | null; acreage?: number | null
  is_primary: boolean; is_active: boolean; created_at: string
}
export const locationsApi = {
  list:   () => api.get<{ locations: Location[] }>('/locations'),
  get:    (id: string) => api.get<Location>(`/locations/${id}`),
  create: (l: Partial<Location>) => api.post<Location>('/locations', l),
  update: (id: string, l: Partial<Location>) => api.put<Location>(`/locations/${id}`, l),
  remove: (id: string) => api.delete(`/locations/${id}`),
}

// ── AI photo diagnosis (feature #3) ─────────────────────────────────────────
export interface DiagnosisResult {
  summary: string
  problems: { name: string; likelihood: string; detail: string; confidence: number }[]
  suggested_tasks: { title: string; priority: string; detail: string }[]
  suggested_yield_events: { type: string; severity: string; note: string }[]
  ai_used: boolean
  warning?: string
}
export const diagnosisApi = {
  // form must contain: image (File), concern (string), plant_label (string, optional)
  diagnose: (form: FormData) =>
    api.post<DiagnosisResult>('/ai/diagnose', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
}

// AI tree detection: a drawn/viewport bbox → tree GPS points from satellite imagery.
export interface DetectedTree { lat: number; lng: number; confidence: number }
// Which in-UX detection pickers to show (superadmin-gated, §179).
export interface DetectOptions {
  engine_picker: boolean
  model_picker: boolean
  engines: string[]        // selectable engines: 'gemini' [+ 'deepforest' if configured]
  models: string[]         // selectable Gemini models
  default_engine: string
  default_model: string
}
export const treeDetectApi = {
  // Pass a bbox (viewport) OR a polygon (a block/zone ring of [lng,lat]) to clip
  // detections to the drawn area instead of the whole view. engine/model are honored
  // only when their superadmin picker flag is on (§179).
  detect: (b: { min_lng?: number; min_lat?: number; max_lng?: number; max_lat?: number; polygon?: number[][]; engine?: string; model?: string }) =>
    // Detection is a slow batch op (tiled multi-model inference; DeepForest adds a
    // cold start). Override the 30s global axios timeout so big parcels don't error
    // with "timeout of 30000ms exceeded" mid-scan.
    api.post<{ trees: DetectedTree[]; count: number; zoom: number; ai_used: boolean; engine?: string; model?: string; warning?: string }>('/ai/detect-trees', b, { timeout: 180_000 }),
  // Detect trees on a stored drone orthomosaic (higher-res than satellite, §229). Same
  // response shape as detect(); engine/model honored only when their picker flag is on.
  detectDrone: (b: { drone_map_id: string; engine?: string; model?: string }) =>
    api.post<{ trees: DetectedTree[]; count: number; zoom: number; ai_used: boolean; engine?: string; model?: string; warning?: string }>('/ai/detect-trees/drone', b, { timeout: 180_000 }),
  options: () => api.get<DetectOptions>('/ai/detect-trees/options'),
}

// Minimal clients to turn AI diagnosis suggestions into real records.
export const tasksApi = {
  list:   (status?: string) => api.get('/tasks', { params: { status } }),
  create: (t: Record<string, unknown>) => api.post('/tasks', t),
  remove: (id: string) => api.delete(`/tasks/${id}`),
}
export const yieldImpactApi = {
  create: (e: Record<string, unknown>) => api.post('/yield-impact', e),
}

// ── AI document/receipt import (feature #4) ─────────────────────────────────
export interface DocImportResult {
  mode: 'entities' | 'receipt'
  plants: { label: string; species: string; variety: string; status: string; planted_at: string; notes: string }[]
  blocks: { name: string; variety: string; tracking_mode: string; plant_count: number }[]
  events: { action_type: string; occurred_at: string; description: string; quantity?: number | null; unit?: string }[]
  receipt?: { vendor: string; date: string; total?: number | null; currency: string; category: string; line_items: { description: string; amount?: number | null }[] }
  ai_used: boolean
  warning?: string
  quota_exceeded?: boolean
}
export const docImportApi = {
  // form: file (File), mode ('entities' | 'receipt')
  importDoc: (form: FormData) =>
    api.post<DocImportResult>('/ai/import-doc', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
}

// ── Global growable reference (seeded catalog: mango/avocado/citrus/banana/
// strawberry/orchid/Garcinia/…). Tenant-readable at /varieties/reference. ──────
export interface GrowableRef {
  id: string; common_name: string; scientific_name: string; latin_name?: string
  family?: string; category: string; flavor_profile?: string
  aliases_common?: string[]; ripe_months?: number[]; tags?: string[]
  is_verified?: boolean; proposed?: boolean  // proposed = this tenant's own pending custom crop
}
// A crop chip from the picker: a picked catalog crop (growable_id set) or a custom one (name only).
export interface CropChip { name: string; growable_id?: string }
// Proposed-crop queue row (superadmin).
export interface ProposedGrowable {
  id: string; common_name: string; category: string; tenant_id: string; farm_name: string; created_at: string
}
export const growableRefApi = {
  list: () => api.get<{ reference: GrowableRef[] }>('/varieties/reference'),
  // Tenant proposes a custom crop → usable now, queued for superadmin promotion.
  propose: (name: string, category?: string) =>
    api.post<GrowableRef>('/varieties/reference/propose', { name, category }),
}

// ── Varieties ────────────────────────────────────────────────
export const varietiesApi = {
  list: (category?: string) =>
    api.get<{ varieties: Variety[]; total: number }>('/varieties', { params: { category } }),
  get: (id: string) => api.get<Variety>(`/varieties/${id}`),
  create: (data: Partial<Variety>) => api.post<Variety>('/varieties', data),
  update: (id: string, data: Partial<Variety>) => api.put<void>(`/varieties/${id}`, data),
  delete: (id: string) => api.delete<void>(`/varieties/${id}`),
  // Materialize a crop-chip list into varieties (onboarding).
  fromCrops: (crops: CropChip[]) => api.post<{ created: number }>('/varieties/from-crops', { crops }),
}

// ── AI pricing (regional retail/wholesale defaults + recommendations) ─────────
// Prices are cents PER KG (same unit as harvest_sales.unit_price_cents), so the
// harvest→sale form can auto-fill from a variety's default.
export interface VarietyPrice {
  variety_id: string; variety_name: string; category: string
  avg_weight_grams?: number | null
  retail_cents_per_kg?: number | null
  wholesale_cents_per_kg?: number | null
  currency: string
  retail_source: string      // manual | ai_estimate | ai_adjusted | ''
  wholesale_source: string
  region: string
  ai_estimated_at?: string | null
  updated_at?: string | null
}
export interface PriceEstimate {
  variety_id: string; variety_name: string
  retail_cents_per_kg?: number | null
  wholesale_cents_per_kg?: number | null
  confidence: string; rationale: string; seeded: boolean
}
export interface PriceEstimateResult {
  region: string; estimates: PriceEstimate[]; seeded: number
  ai_used: boolean; warning?: string
}
export interface PriceRecommendation {
  id: string; variety_id: string; variety_name: string
  channel: string                       // retail | wholesale
  current_cents_per_kg?: number | null
  recommended_cents_per_kg: number
  direction: string                     // up | down | hold
  rationale: string; confidence: string; region: string
  status: string; created_at: string; decided_at?: string | null
}
export const pricingApi = {
  list: () => api.get<{ prices: VarietyPrice[] }>('/pricing/varieties'),
  setPrice: (varietyId: string, body: { retail_cents_per_kg?: number | null; wholesale_cents_per_kg?: number | null }) =>
    api.put<VarietyPrice>(`/pricing/varieties/${varietyId}`, body),
  estimate: (varietyId: string, region?: string) =>
    api.post<PriceEstimateResult>(`/pricing/varieties/${varietyId}/estimate`, { region }),
  estimateMissing: (region?: string) =>
    api.post<PriceEstimateResult>('/pricing/estimate-missing', { region }),
  recommendations: (status?: string) =>
    api.get<{ recommendations: PriceRecommendation[] }>('/pricing/recommendations', { params: { status } }),
  refreshRecommendations: (region?: string) =>
    api.post<{ region: string; recommendations: PriceRecommendation[]; created: number; ai_used: boolean; warning?: string }>(
      '/pricing/recommendations/refresh', { region }),
  applyRecommendation: (id: string) => api.post<void>(`/pricing/recommendations/${id}/apply`, {}),
  dismissRecommendation: (id: string) => api.post<void>(`/pricing/recommendations/${id}/dismiss`, {}),
}

// ── Plants ───────────────────────────────────────────────────
export const plantsApi = {
  list: (params?: { zone_id?: string; variety_id?: string; status?: string }) =>
    api.get<{ plants: Plant[]; total: number }>('/plants', { params }),
  get: (id: string) => api.get<Plant>(`/plants/${id}`),
  create: (data: Partial<Plant>) => api.post<Plant>('/plants', data),
  update: (id: string, data: Partial<Plant>) => api.put<void>(`/plants/${id}`, data),
  // Soft-delete (deleted_at) — removes the plant from lists/map but keeps the row
  // + history in the DB. Distinct from setting status='dead' (which keeps it shown).
  delete: (id: string) => api.delete<void>(`/plants/${id}`),
  // Bulk-assign selected plants to a block, plant zone, and/or irrigation zone.
  assign: (plant_ids: string[], target: { block_id?: string; irrigation_zone_id?: string; zone_id?: string }) =>
    api.post<{ updated: number }>('/plants/assign', { plant_ids, ...target }),
  history: (id: string) => api.get<Observation[]>(`/plants/${id}/history`),
  addObservation: (id: string, data: Partial<Observation>) =>
    api.post<void>(`/plants/${id}/observations`, data),
  fertilizerSchedule: (id: string) =>
    api.get<FertilizerSchedule[]>(`/plants/${id}/fertilizer-schedule`),
}

// ── Parcels (free, keyless statewide GIS property-boundary lookup) ────────────
export const parcelsApi = {
  sources: () => api.get<{ states: { state: string; name: string }[] }>('/parcels/sources'),
  lookup: (lat: number, lng: number, state: string) =>
    api.get<{ found: boolean; state: string; source?: string; geometry?: GeoJSON; properties?: Record<string, unknown>; warning?: string }>(
      '/parcels/lookup', { params: { lat, lng, state } }),
  // Is there a free parcel source for this location? Drives Find-vs-Draw boundary UI.
  coverage: (lat: number, lng: number, state: string) =>
    api.get<{ covered: boolean; kind: 'state' | 'county' | 'none'; state: string; county?: string; source?: string }>(
      '/parcels/coverage', { params: { lat, lng, state } }),
  // All parcels intersecting a bbox → GeoJSON FeatureCollection ("show lot lines").
  area: (b: { min_lng: number; min_lat: number; max_lng: number; max_lat: number }, state: string) =>
    api.get<{ found: boolean; count: number; source?: string; geojson?: GeoJSON; warning?: string }>(
      '/parcels/area', { params: { ...b, state } }),
}

// ── Soil at a point (keyless USDA-NRCS Soil Data Access) ────────────────────────
export interface SoilPoint {
  found: boolean; map_unit?: string; component?: string; component_pct?: number
  drainage_class?: string; taxonomy?: string; farmland_class?: string; source?: string; warning?: string
}
export const soilApi = {
  point: (lat: number, lng: number) => api.get<SoilPoint>('/soil-survey/point', { params: { lat, lng } }),
}

// ── Farm Map ─────────────────────────────────────────────────
export interface TreePlacement {
  lat: number; lng: number; label: string
  species_id?: string; species_name?: string; scientific_name?: string; block_id?: string; irrigation_zone_id?: string; zone_id?: string
}
// Deep-link a printed field QR to the in-app quick-record sheet. Login-gated
// (the worker signs in once on their phone); the SPA route is /r/plant|block/:id
// (see QuickRecordPage). origin = wherever the SPA is served, so the scan lands
// back in the same app the sticker was printed from.
export const recordLink = (kind: 'plant' | 'block', id: string) =>
  `${window.location.origin}/r/${kind}/${id}`
export const farmMapApi = {
  listFeatures: (type?: string) =>
    api.get<{ features: FarmFeature[] }>('/map/features', { params: { type } }),
  createFeature: (data: Partial<FarmFeature>) => api.post<FarmFeature>('/map/features', data),
  // Bulk-place many trees (each → a pin + linked Plant) in ONE request. Used by
  // AI tree detection "Place N" — avoids the per-IP rate limiter that ~200 trees
  // hit when placed one-by-one (3 calls each).
  placeTreesBatch: (body: { location_id?: string | null; trees: TreePlacement[] }) =>
    api.post<{ created: number }>('/map/trees/batch', body),
  // Convert ALL legacy orphan tree pins → Plants in ONE request (server-side
  // set-based). Replaces the per-pin convert loop that hit the rate limiter.
  backfillTrees: () => api.post<{ converted: number }>('/map/trees/backfill', {}),

  // Bulk-delete tree pins (marquee multi-select) in ONE request — soft-deletes the
  // pins + their linked plants server-side, avoiding the per-pin DELETE 429.
  deleteTreesBatch: (ids: string[]) => api.post<{ deleted: number }>('/map/trees/delete', { ids }),

  // Bulk variety + sequential naming for a marquee selection (one request).
  // species_* recolours the pins; names = { featureId: label } renames linked plants.
  bulkEditTrees: (body: { feature_ids: string[]; species_id?: string; species_name?: string; scientific_name?: string; names?: Record<string, string> }) =>
    api.post<{ updated: number }>('/map/trees/bulk-edit', body),
  updateFeature: (id: string, data: Partial<FarmFeature>) =>
    api.put<void>(`/map/features/${id}`, data),
  deleteFeature: (id: string) => api.delete<void>(`/map/features/${id}`),
  listZones: () => api.get<{ zones: FarmFeature[] }>('/map/zones'),
  createZone: (data: Partial<FarmFeature>) => api.post<FarmFeature>('/map/zones', data),
  updateSatelliteBounds: (bounds: SatelliteBounds) =>
    api.put<void>('/map/satellite-bounds', bounds),
}

// ── Harvests ─────────────────────────────────────────────────
export const harvestsApi = {
  list: (params?: { season?: string; variety_id?: string }) =>
    api.get<{ harvests: HarvestEvent[] }>('/harvests', { params }),
  create: (data: Partial<HarvestEvent>) => api.post<HarvestEvent>('/harvests', data),
  get: (id: string) => api.get<HarvestEvent>(`/harvests/${id}`),
  update: (id: string, data: Partial<HarvestEvent>) => api.put<void>(`/harvests/${id}`, data),
  summaryByVariety: () => api.get<VarietySummary[]>('/harvests/summary/by-variety'),
  summaryBySeason: () => api.get<SeasonSummary[]>('/harvests/summary/by-season'),
  recordSale: (id: string, data: SaleInput) => api.post<Sale>(`/harvests/${id}/sales`, data),
  listSales: (id: string) => api.get<{ sales: Sale[] }>(`/harvests/${id}/sales`),
}

// ── Spoilage / crop-loss tracking (§55.7) ───────────────────────────────────────
export type SpoilageType = 'not_picked' | 'not_sold' | 'grade_out'
export interface SpoilageEvent {
  id: string; spoilage_type: SpoilageType; variety_id?: string; variety_name?: string
  block_id?: string; block_name?: string; zone_id?: string; plant_id?: string
  quantity_kg: number; value_usd?: number; reason?: string; season?: string
  occurred_at: string; notes?: string; created_at: string
}
export interface SpoilageBucket { key: string; label: string; quantity_kg: number; value_usd: number; count: number }
export interface SpoilageReport {
  from: string; to: string; total_kg: number; total_value: number; count: number
  by_type: SpoilageBucket[]; by_variety: SpoilageBucket[]; by_reason: SpoilageBucket[]; by_block: SpoilageBucket[]
}
export const spoilageApi = {
  list: (params?: { type?: string; variety_id?: string; season?: string }) =>
    api.get<{ events: SpoilageEvent[] }>('/spoilage', { params }),
  create: (data: Partial<SpoilageEvent>) => api.post<SpoilageEvent>('/spoilage', data),
  remove: (id: string) => api.delete<void>(`/spoilage/${id}`),
  report: (params?: { from?: string; to?: string }) => api.get<SpoilageReport>('/spoilage/report', { params }),
}

// ── Containers / bins / pallets + traceability (§209) ──────────────────────────
export interface Container {
  id: string; container_type: string; label: string; capacity_lb?: number; weight_lb?: number
  status: string; parent_id?: string; parent_label?: string; harvest_id?: string
  block_id?: string; block_name?: string; variety_id?: string; variety_name?: string
  child_count: number; filled_at?: string; notes?: string; created_at: string
}
export interface ContainerChild { id: string; label: string; container_type: string; status: string; weight_lb?: number }
export interface ContainerProvenance {
  container_id: string; label: string; container_type: string; status: string
  source: 'harvest' | 'container' | 'none'; block_name?: string; variety_name?: string
  harvested_at?: string; grade?: string; brix?: number; parent_label?: string; children: ContainerChild[]
}
export interface RecallOrder {
  order_id: string; customer_name?: string; customer_email?: string; customer_phone?: string
  status?: string; created_at: string; container_id: string; container_label?: string
}
export interface RecallReport {
  container_id: string; label: string; containers_searched: number; orders: RecallOrder[]
}
export const containersApi = {
  list: (params?: { type?: string; status?: string; parent?: string }) =>
    api.get<{ containers: Container[] }>('/containers', { params }),
  get: (id: string) => api.get<Container>(`/containers/${id}`),
  create: (data: Partial<Container>) => api.post<Container>('/containers', data),
  update: (id: string, data: Partial<Container>) => api.put<Container>(`/containers/${id}`, data),
  remove: (id: string) => api.delete<void>(`/containers/${id}`),
  trace: (id: string) => api.get<ContainerProvenance>(`/containers/${id}/trace`),
  recall: (id: string) => api.get<RecallReport>(`/containers/${id}/recall`),
  // Order ↔ container fulfillment linkage (downstream recall chain).
  orderContainers: (orderId: string) =>
    api.get<{ containers: Container[] }>('/order-containers', { params: { order_id: orderId } }),
  linkOrder: (orderId: string, containerId: string) =>
    api.post<{ status: string }>('/order-containers', { order_id: orderId, container_id: containerId }),
  unlinkOrder: (orderId: string, containerId: string) =>
    api.delete<{ status: string }>('/order-containers', { params: { order_id: orderId, container_id: containerId } }),
}

// ── Customer CRM (§165) ──────────────────────────────────────────────────────
export interface Customer {
  id: string; name: string; email: string; phone: string; address: string
  email_opt_in: boolean; sms_opt_in: boolean; marketing_opt_in: boolean
  notes: string; source: string
  order_count: number; total_spent: number; last_order_at?: string; created_at: string
}
export interface CustomerOrder {
  id: string; total_amount: number; status?: string; fulfillment_method?: string; created_at: string
}
export interface CustomerReservation {
  id: string; item_id: string; item_name: string; quantity: number; unit_label: string
  status: string; expected_from?: string; expected_to?: string; created_at: string
}
export interface Campaign {
  id: string; subject: string; body: string
  recipient_count: number; sent_count: number; status: string; created_at: string
}
export interface SMSResult { recipient_count: number; sent_count: number; configured: boolean }
export const customersApi = {
  list: (search?: string) =>
    api.get<{ customers: Customer[] }>('/customers', { params: search ? { search } : undefined }),
  get: (id: string) => api.get<Customer>(`/customers/${id}`),
  orders: (id: string) => api.get<{ orders: CustomerOrder[] }>(`/customers/${id}/orders`),
  reservations: (id: string) => api.get<{ reservations: CustomerReservation[] }>(`/customers/${id}/reservations`),
  create: (data: Partial<Customer>) => api.post<Customer>('/customers', data),
  update: (id: string, data: Partial<Customer>) => api.put<Customer>(`/customers/${id}`, data),
  remove: (id: string) => api.delete<void>(`/customers/${id}`),
  importFromOrders: () => api.post<{ imported: number }>('/customers/import', {}),
  // Email blast to opted-in customers (§166).
  campaigns: () => api.get<{ campaigns: Campaign[] }>('/customers/campaigns'),
  sendCampaign: (body: { subject: string; body: string }) => api.post<Campaign>('/customers/campaigns', body),
  // SMS blast to opted-in customers (§174/§224). 429 when over the monthly quota; configured=false → nothing sent.
  // kind ∈ blast|availability (§55.14) — recorded in the SMS log.
  sendSMS: (body: { body: string; kind?: 'blast' | 'availability' }) => api.post<SMSResult>('/customers/sms', body),
  // AI "draft with AI" SMS copy (§224) — 402 off plan / 429 over the ai_marketing_copy quota.
  draftSMS: (topic: string) => api.post<{ draft: string; source: string }>('/customers/sms/draft', { topic }),
  // Public one-click unsubscribe (no auth — token + tenant id are the credential).
  unsubscribe: (tenantId: string, token: string) =>
    api.post<{ ok: boolean }>('/public/unsubscribe', { tenant_id: tenantId, token }),
}

// ── Pre-orders / reservations (§167) ─────────────────────────────────────────
export interface PreorderItem {
  id: string; name: string; variety_id?: string; variety_name?: string; description?: string
  unit_label: string; price?: number; expected_from?: string; expected_to?: string
  capacity: number; min_quantity: number; reserved: number; reservation_count: number
  status: 'open' | 'ready' | 'closed' | 'cancelled'; is_published: boolean; notes?: string; created_at: string
}
export interface PreorderReservation {
  id: string; item_id: string; item_name?: string; customer_id?: string
  customer_name: string; customer_email?: string; customer_phone?: string
  quantity: number; status: 'pending' | 'confirmed' | 'fulfilled' | 'cancelled'
  source: string; notes?: string; notified_at?: string; created_at: string
}
export interface PreorderQuota { monthly_limit: number; monthly_used: number; remaining?: number; reset_at?: string }
export const preordersApi = {
  list: (status?: string) =>
    api.get<{ items: PreorderItem[] }>('/preorders', { params: status ? { status } : undefined }),
  quota: () => api.get<PreorderQuota>('/preorders/quota'),
  get: (id: string) => api.get<PreorderItem>(`/preorders/${id}`),
  create: (data: Partial<PreorderItem>) => api.post<PreorderItem>('/preorders', data),
  update: (id: string, data: Partial<PreorderItem>) => api.put<PreorderItem>(`/preorders/${id}`, data),
  remove: (id: string) => api.delete<void>(`/preorders/${id}`),
  markReady: (id: string) => api.post<{ notified: number }>(`/preorders/${id}/ready`, {}),
  reservations: (itemId?: string) =>
    api.get<{ reservations: PreorderReservation[] }>('/preorders/reservations', { params: itemId ? { item_id: itemId } : undefined }),
  addReservation: (data: { item_id: string; customer_name: string; customer_email?: string; customer_phone?: string; quantity: number; notes?: string }) =>
    api.post<PreorderReservation>('/preorders/reservations', data),
  setReservationStatus: (id: string, status: string) =>
    api.patch<{ status: string }>(`/preorders/reservations/${id}`, { status }),
}

// ── Support tickets (§197) ───────────────────────────────────────────────────
export type TicketStatus = 'open' | 'in_progress' | 'waiting_on_customer' | 'resolved' | 'closed'
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent'
export interface TicketReply {
  id: string; ticket_id: string; author_id?: string; author_name: string
  is_staff: boolean; body: string; created_at: string
}
export interface SupportTicket {
  id: string; tenant_id: string; tenant_name?: string; submitted_by: string
  subject: string; body: string; category: string
  kind?: string; rating?: number | null; page_url?: string  // §221: feedback vs support
  status: TicketStatus; priority: TicketPriority
  resolved_at?: string; reply_count: number; created_at: string; updated_at: string
  replies?: TicketReply[]
}
export const supportApi = {
  list: () => api.get<{ tickets: SupportTicket[] }>('/support/tickets'),
  get: (id: string) => api.get<SupportTicket>(`/support/tickets/${id}`),
  create: (data: { subject: string; body: string; category: string; priority: TicketPriority; kind?: string; rating?: number | null; page_url?: string }) =>
    api.post<SupportTicket>('/support/tickets', data),
  reply: (id: string, body: string) => api.post<TicketReply>(`/support/tickets/${id}/replies`, { body }),
  close: (id: string) => api.post<{ status: string }>(`/support/tickets/${id}/close`, {}),
  // Superadmin (staff) cross-tenant queue; kind = '' | 'support' | 'feedback'
  staffList: (kind?: string) => api.get<{ tickets: SupportTicket[] }>('/admin/support/tickets', { params: kind ? { kind } : undefined }),
  staffGet: (tenantId: string, id: string) => api.get<SupportTicket>(`/admin/support/tickets/${tenantId}/${id}`),
  staffReply: (tenantId: string, id: string, body: string) =>
    api.post<TicketReply>(`/admin/support/tickets/${tenantId}/${id}/replies`, { body }),
  staffSetStatus: (tenantId: string, id: string, status: TicketStatus) =>
    api.post<{ status: string }>(`/admin/support/tickets/${tenantId}/${id}/status`, { status }),
}

// ── Ripening tracking (§53) — per-plant stage observations + live predictions ───
export const RIPENING_STAGES = [
  'flower_bud', 'flowering', 'fruit_set', 'immature', 'mature_green',
  'color_break', 'ripe', 'overripe', 'dropped',
] as const
export type RipeningStage = typeof RIPENING_STAGES[number]
export interface RipeningObservation {
  id: string; plant_id: string; plant_label?: string; variety_name?: string
  stage: RipeningStage; color_description: string; size_cm?: number
  firmness: string; brix?: number; notes: string; photo_urls: string[]
  recorded_by?: string; observed_at: string; created_at: string
}
export interface RipeningPrediction {
  plant_id: string; plant_label: string; variety_name: string
  current_stage?: string; last_observed_at?: string
  predicted_start?: string; predicted_peak?: string; predicted_end?: string
  days_until_peak?: number; confidence_pct: number
  source: string; reasoning: string
}
export interface RipeningOutlook {
  narrative: string
  source: 'vertex' | 'fallback'
  prediction_count: number
  disclaimer: string
}

export const ripeningApi = {
  predictions: () => api.get<{ predictions: RipeningPrediction[] }>('/ripening/predictions'),
  outlook: () => api.get<RipeningOutlook>('/ripening/outlook'),
  observations: (plantId?: string) =>
    api.get<{ observations: RipeningObservation[] }>('/ripening/observations',
      plantId ? { params: { plant_id: plantId } } : undefined),
  logObservation: (data: Partial<RipeningObservation>) =>
    api.post<RipeningObservation>('/ripening/observations', data),
}

// ── AI insights: yield + weather analysis (Gemini + deterministic fallback) ─────
export interface VarietyKg { name: string; kg: number }
export interface YieldAnalysis {
  narrative: string
  source: 'vertex' | 'fallback'
  total_kg: number
  months: number
  top_varieties: VarietyKg[]
  weather_correlation?: number
  disclaimer: string
}
export interface WeatherAnalysis {
  narrative: string
  source: 'vertex' | 'fallback'
  months: number
  avg_temp_c?: number
  total_rainfall_mm?: number
  frost_months: number
  disclaimer: string
}
export interface CategoryCount { category: string; count: number }
export interface PlantingRecommendation {
  narrative: string
  source: 'vertex' | 'fallback'
  region: string
  current_categories: CategoryCount[]
  disclaimer: string
}
export interface AIUsageFeature { feature: string; calls: number; tokens: number; cost_usd: number }
export interface AIUsageSummary {
  month_usd: number
  total_calls: number
  by_feature: AIUsageFeature[]
  disclaimer: string
}
export interface AIBudget {
  monthly_budget_usd: number
  alert_threshold_usd: number
  month_to_date_usd: number
  over_threshold: boolean
  over_budget: boolean
  disclaimer: string
}
export const aiInsightsApi = {
  analyzeYield: () => api.post<YieldAnalysis>('/ai/analyze/yield', {}),
  analyzeWeather: () => api.post<WeatherAnalysis>('/ai/analyze/weather', {}),
  recommendPlanting: () => api.get<PlantingRecommendation>('/ai/recommendations/planting'),
  usage: () => api.get<AIUsageSummary>('/ai/usage'),
  getBudget: () => api.get<AIBudget>('/ai/budget'),
  setBudget: (monthly_budget_usd: number, alert_threshold_usd: number) =>
    api.put<AIBudget>('/ai/budget', { monthly_budget_usd, alert_threshold_usd }),
}

// ── Team / RBAC (§35) — members, roles, seat usage ──────────────────────────────
export type TeamRole = 'owner' | 'admin' | 'irrigation_manager' | 'irrigation_operator' | 'zone_manager' | 'farm_hand' | 'viewer'
export interface TeamMember {
  user_id: string; tenant_id: string; email: string; display_name: string; avatar_url: string
  status: string; auth_provider: string; roles: TeamRole[]; seat_type: string
  last_login_at?: string; invited_at?: string; created_at: string
}
export interface SeatUsage {
  tenant_id: string
  included_full_seats: number; included_farmhand_seats: number
  used_full_seats: number; used_farmhand_seats: number
  full_seat_price_usd: number; farmhand_seat_price_usd: number
  overage_full_seats: number; overage_farmhand_seats: number
  monthly_overage_usd: number; unlimited: boolean
}
export interface InviteResult { user_id: string; email: string; status: 'invited' | 'added' | 'needs_entitlement'; email_sent: boolean; reason?: string }
// Shareable invite link + join-request approval (§206)
export interface InviteLink { code: string; url: string; default_role: string; expires_at?: string }
export interface JoinRequest { id: string; tenant_id: string; user_id: string; email: string; display_name: string; provider: string; status: string; requested_at: string }
export const teamApi = {
  list: () => api.get<TeamMember[]>('/team'),
  roles: () => api.get<TeamRole[]>('/team/roles'),
  seats: () => api.get<SeatUsage>('/team/seats'),
  setRoles: (userId: string, roles: TeamRole[]) => api.put<TeamMember>(`/team/${userId}`, { roles }),
  remove: (userId: string) => api.delete<void>(`/team/${userId}`),
  invite: (data: { email: string; name?: string; roles: TeamRole[] }) => api.post<InviteResult>('/team/invite', data),
  // Sponsor a multi-workspace add-on for a person (billed through the active tenant)
  // so they can join despite already being in another free workspace.
  sponsorAddon: (email: string) => api.post('/team/sponsored-addons', { email }),
  // Shareable invite link
  getInviteLink: () => api.get<InviteLink>('/team/invite-link'),
  generateInviteLink: (role?: string) => api.post<InviteLink>('/team/invite-link', role ? { role } : {}),
  // Join requests (people who opened the invite link + signed in)
  joinRequests: () => api.get<{ requests: JoinRequest[] }>('/team/join-requests'),
  approveJoinRequest: (userId: string, role: TeamRole) => api.post<void>(`/team/join-requests/${userId}/approve`, { role }),
  rejectJoinRequest: (userId: string) => api.post<void>(`/team/join-requests/${userId}/reject`),
}

// ── Custom forms (§55.11) — tenant-defined input forms + responses ──────────────
export type FormFieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea'
export interface FormField { key: string; label: string; type: FormFieldType; required?: boolean; options?: string[] }
export interface CustomForm {
  id: string; name: string; description: string; fields: FormField[]
  resource_type: string; is_active: boolean; created_at: string; updated_at: string
}
export interface FormResponse {
  id: string; form_id: string; resource_type?: string; resource_id?: string
  values: Record<string, unknown>; submitted_by?: string; submitted_at: string
}
export const formsApi = {
  list: () => api.get<{ forms: CustomForm[] }>('/forms'),
  create: (data: Partial<CustomForm>) => api.post<CustomForm>('/forms', data),
  update: (id: string, data: Partial<CustomForm>) => api.put<CustomForm>(`/forms/${id}`, data),
  remove: (id: string) => api.delete<void>(`/forms/${id}`),
  responses: (id: string) => api.get<{ responses: FormResponse[] }>(`/forms/${id}/responses`),
  submit: (id: string, data: { resource_type?: string; resource_id?: string; values: Record<string, unknown> }) =>
    api.post<FormResponse>(`/forms/${id}/responses`, data),
  deleteResponse: (id: string) => api.delete<void>(`/form-responses/${id}`),
}

// ── Irrigation ───────────────────────────────────────────────
export interface ZonePlant { plant_id: string; label: string; emitter_gpm: number | null; effective_gpm: number }
export interface ZoneHydraulics {
  zone_id: string; plant_count: number; default_emitter_gpm: number
  input_pipe_size: number; input_pipe_material: string
  total_demand_gpm: number; run_minutes: number; gallons_per_run: number
  pipe_max_gpm: number; utilization_pct: number; over_capacity: boolean
  headroom_gpm: number; max_plants_supported: number; notes?: string[]; plants: ZonePlant[]
}
export interface ZoneMetaPatch {
  name?: string; description?: string; color_hex?: string
  input_pipe_size?: number; input_pipe_material?: string; default_emitter_gpm?: number
  station_number?: number | null // "Zone N" on the controller (zone-as-hub binding, §174)
  geometry?: GeoJSON.Polygon | GeoJSON.Geometry // reshape from the map
}
// What CreateZone accepts — a controller-less map-drawn zone (the named "Zone N" hub).
export interface ZoneCreate { name?: string; color_hex?: string; geometry?: GeoJSON.Polygon | GeoJSON.Geometry; station_number?: number | null }

export const irrigationApi = {
  listZones: () => api.get<{ zones: IrrigationZone[] }>('/irrigation/zones'),
  createZone: (data: ZoneCreate) => api.post<{ id: string }>('/irrigation/zones', data),
  deleteZone: (id: string) => api.delete<void>(`/irrigation/zones/${id}`),
  updateZone: (id: string, data: ZoneMetaPatch) => api.put<{ status: string }>(`/irrigation/zones/${id}`, data),
  zoneHydraulics: (id: string, runMinutes?: number) =>
    api.get<ZoneHydraulics>(`/irrigation/zones/${id}/hydraulics`, { params: { run_minutes: runMinutes } }),
  setPlantEmitter: (plantId: string, emitter_gpm: number | null) =>
    api.put<{ status: string }>(`/irrigation/plants/${plantId}/emitter`, { emitter_gpm }),
  listSchedules: () => api.get<{ schedules: Schedule[] }>('/irrigation/schedules'),
  createSchedule: (data: Partial<Schedule>) => api.post<Schedule>('/irrigation/schedules', data),
  updateSchedule: (id: string, data: Partial<Schedule>) =>
    api.put<void>(`/irrigation/schedules/${id}`, data),
  deleteSchedule: (id: string) => api.delete<void>(`/irrigation/schedules/${id}`),
  listControllers: () =>
    api.get<{ controllers: Controller[]; supported_types: string[] }>('/irrigation/controllers'),
  addController: (data: Record<string, string>) => api.post<Controller>('/irrigation/controllers', data),
  updateController: (id: string, data: Partial<Controller>) =>
    api.put<void>(`/irrigation/controllers/${id}`, data),
  manualRun: (id: string, stationNumber: number, durationMinutes: number) =>
    api.post<void>(`/irrigation/controllers/${id}/run`, { station_number: stationNumber, duration_minutes: durationMinutes }),
  stopAll: (id: string) =>
    api.post<void>(`/irrigation/controllers/${id}/stop`),
  controllerStatus: (id: string) =>
    api.get<{ zones: { station_number: number; is_running: boolean; last_run?: string }[] }>(`/irrigation/controllers/${id}/status`),
  importZones: (id: string) =>
    api.post<{ imported: number }>(`/irrigation/controllers/${id}/import`),
  deleteController: (id: string) =>
    api.delete<void>(`/irrigation/controllers/${id}`),
}

// ── Underground irrigation infrastructure (§172): pipes / manifolds / valves / emitters ──
export interface IrrigationPipe {
  id: string
  name: string
  geometry: GeoJSON.LineString | GeoJSON.Geometry
  pipe_role: 'mainline' | 'lateral' // mainline = always pressurized (before valve); lateral = zone pipe (after valve, on only when the valve is open)
  zone_id?: string | null // irrigation zone a lateral belongs to
  diameter_in?: number | null
  size_label?: string // nominal label e.g. 3/4" or DN20
  size_system?: string // us | metric
  material: string
  depth_in?: number | null
  color_hex: string
  notes: string
}
export interface IrrigationManifold {
  id: string
  name: string
  geometry: GeoJSON.Point | GeoJSON.Geometry
  outlet_count?: number // physical valve ports on this manifold
  notes: string
}
export interface IrrigationValve {
  id: string
  name: string
  geometry?: GeoJSON.Point | GeoJSON.Geometry | null
  manifold_id?: string | null
  zone_id?: string | null
  pipe_id?: string | null
  valve_type: string
  output_psi?: number | null // regulated pressure at the valve outlet
  model_id?: string | null // catalog model (irrigation_valve_models)
  flow_control?: boolean
  normally_open: boolean
  notes: string
}
export interface PlantEmitter {
  id: string
  plant_id: string
  emitter_type: string // drip | bubbler | sprinkler | micro_spray | soaker | other
  gpm?: number | null
  count: number
  spacing_in?: number | null
  notes: string
}
export interface IrrigationSource {
  id: string
  name: string
  geometry: GeoJSON.Point | GeoJSON.Geometry
  kind: string // well_pump | city_water | booster_pump | tank | pond | other
  output_gpm?: number | null
  output_psi?: number | null
  pipe_id?: string | null
  notes: string
}
export const irrigationInfraApi = {
  listPipes: () => api.get<{ pipes: IrrigationPipe[] }>('/irrigation/pipes'),
  createPipe: (d: Partial<IrrigationPipe>) => api.post<IrrigationPipe>('/irrigation/pipes', d),
  updatePipe: (id: string, d: Partial<IrrigationPipe>) => api.put<void>(`/irrigation/pipes/${id}`, d),
  deletePipe: (id: string) => api.delete<void>(`/irrigation/pipes/${id}`),
  listManifolds: () => api.get<{ manifolds: IrrigationManifold[] }>('/irrigation/manifolds'),
  createManifold: (d: Partial<IrrigationManifold>) => api.post<IrrigationManifold>('/irrigation/manifolds', d),
  updateManifold: (id: string, d: Partial<IrrigationManifold>) => api.put<void>(`/irrigation/manifolds/${id}`, d),
  deleteManifold: (id: string) => api.delete<void>(`/irrigation/manifolds/${id}`),
  listValves: () => api.get<{ valves: IrrigationValve[] }>('/irrigation/valves'),
  createValve: (d: Partial<IrrigationValve>) => api.post<IrrigationValve>('/irrigation/valves', d),
  updateValve: (id: string, d: Partial<IrrigationValve>) => api.put<void>(`/irrigation/valves/${id}`, d),
  deleteValve: (id: string) => api.delete<void>(`/irrigation/valves/${id}`),
  listEmitters: (plantId?: string) =>
    api.get<{ emitters: PlantEmitter[] }>('/irrigation/emitters', { params: plantId ? { plant_id: plantId } : {} }),
  createEmitter: (d: Partial<PlantEmitter>) => api.post<PlantEmitter>('/irrigation/emitters', d),
  updateEmitter: (id: string, d: Partial<PlantEmitter>) => api.put<void>(`/irrigation/emitters/${id}`, d),
  deleteEmitter: (id: string) => api.delete<void>(`/irrigation/emitters/${id}`),
  setPlantIrrigation: (plantId: string, enabled: boolean) =>
    api.put<{ status: string }>(`/irrigation/plants/${plantId}/irrigation`, { enabled }),
  pipeSizes: () => api.get<{ sizes: { system: string; label: string; diameter_in: number }[] }>('/irrigation/pipe-sizes'),
  valveModels: () => api.get<{ models: ValveModel[] }>('/irrigation/valve-models'),
  // Water sources (well pumps + city-water ingress) on the irrigation layer (§174)
  listSources: () => api.get<{ sources: IrrigationSource[] }>('/irrigation/sources'),
  createSource: (d: Partial<IrrigationSource>) => api.post<IrrigationSource>('/irrigation/sources', d),
  updateSource: (id: string, d: Partial<IrrigationSource>) => api.put<void>(`/irrigation/sources/${id}`, d),
  deleteSource: (id: string) => api.delete<void>(`/irrigation/sources/${id}`),
  // AI estimate: output GPM + outlet PSI at a target head count (§173, AI-gated; deterministic fallback)
  aiEstimate: (zoneId: string, targetHeads: number) =>
    api.post<ZoneEstimate>(`/irrigation/zones/${zoneId}/ai-estimate`, { target_heads: targetHeads }),
}
export interface ZoneEstimate {
  zone_id: string
  target_heads: number
  emitter_gpm: number
  requested_gpm: number
  deliverable_gpm: number
  pipe_max_gpm: number
  supply_psi: number
  supply_psi_assumed: boolean
  estimated_outlet_psi: number
  over_capacity: boolean
  narrative: string
  source: 'vertex' | 'fallback'
  disclaimer: string
}
export interface ValveModel {
  id: string
  brand: string
  model: string
  valve_type: string
  size_in?: number | null
  has_flow_control: boolean
  flow_min_gpm?: number | null
  flow_max_gpm?: number | null
  notes: string
}

// ── Structures (barns, well houses, packing houses, … color-coded map layer, §174) ─
export interface Structure {
  id: string
  name: string
  geometry: GeoJSON.Polygon | GeoJSON.Point | GeoJSON.Geometry // resizable polygon (or point)
  structure_type: string // barn|well_house|equipment_shed|packing_house|stable|greenhouse|shed|silo|coop|pen|garage|office|residence|water_tank|other
  color_hex: string
  notes: string
}
export const structuresApi = {
  list: () => api.get<{ structures: Structure[] }>('/structures'),
  create: (d: Partial<Structure>) => api.post<Structure>('/structures', d),
  update: (id: string, d: Partial<Structure>) => api.put<void>(`/structures/${id}`, d),
  remove: (id: string) => api.delete<void>(`/structures/${id}`),
}

// ── Propagation ──────────────────────────────────────────────
export const propagationApi = {
  listBatches: () => api.get<{ batches: PropagationBatch[] }>('/propagation/batches'),
  createBatch: (data: Partial<PropagationBatch>) =>
    api.post<PropagationBatch>('/propagation/batches', data),
  getBatch: (id: string) => api.get<PropagationBatch>(`/propagation/batches/${id}`),
  updateBatch: (id: string, data: Partial<PropagationBatch>) =>
    api.put<void>(`/propagation/batches/${id}`, data),
  listSeedlings: (batchId: string) =>
    api.get<Seedling[]>(`/propagation/batches/${batchId}/seedlings`),
  addSeedlings: (batchId: string, count: number) =>
    api.post<void>(`/propagation/batches/${batchId}/seedlings`, { count }),
  updateSeedling: (batchId: string, seedlingId: string, data: Partial<Seedling>) =>
    api.put<void>(`/propagation/batches/${batchId}/seedlings/${seedlingId}`, data),
  recordGraft: (batchId: string, data: Partial<GraftRecord>) =>
    api.post<void>(`/propagation/batches/${batchId}/grafts`, data),
  listGrafts: (batchId: string) =>
    api.get<GraftRecord[]>(`/propagation/batches/${batchId}/grafts`),
  successRates: () => api.get<SuccessRateSummary[]>('/propagation/stats/success-rates'),
}

// ── Weather ──────────────────────────────────────────────────
export const weatherApi = {
  current: () => api.get<WeatherObservation>('/weather/current'),
  history: (days = 30) => api.get<WeatherObservation[]>('/weather/history', { params: { days } }),
  et: () => api.get<{ et_mm: number }>('/weather/et'),
  stations: () => api.get<WeatherStation[]>('/weather/stations'),
  addStation: (data: Partial<WeatherStation>) => api.post<WeatherStation>('/weather/stations', data),
}

// ── Frost risk (§186) — upcoming frost/freeze nights from the NWS forecast ──────
export type FrostLevel = 'freeze' | 'frost' | 'watch' | 'none'
export interface FrostNight { date: string; label: string; low_f: number; level: FrostLevel; short_forecast: string }
export interface FrostForecast {
  found: boolean; location_id?: string; location?: string
  nights: FrostNight[]; next_frost?: FrostNight; next_freeze?: FrostNight; message?: string
}
export const frostApi = {
  forecast: () => api.get<FrostForecast>('/frost/forecast'),
}

// ── Inventory ────────────────────────────────────────────────
export const inventoryApi = {
  list: (category?: string) =>
    api.get<{ items: InventoryItem[] }>('/inventory', { params: { category } }),
  create: (data: Partial<InventoryItem>) => api.post<InventoryItem>('/inventory', data),
  update: (id: string, data: Partial<InventoryItem>) => api.put<void>(`/inventory/${id}`, data),
  recordTransaction: (id: string, data: Partial<InventoryTransaction>) =>
    api.post<void>(`/inventory/${id}/transactions`, data),
  listFertilizerSchedules: () =>
    api.get<FertilizerSchedule[]>('/inventory/fertilizer-schedules'),
  createFertilizerSchedule: (data: Partial<FertilizerSchedule>) =>
    api.post<FertilizerSchedule>('/inventory/fertilizer-schedules', data),
}

// ── Commerce ─────────────────────────────────────────────────
export const commerceApi = {
  listProducts: () => api.get<Product[]>('/commerce/products'),
  createProduct: (data: Partial<Product>) => api.post<Product>('/commerce/products', data),
  updateProduct: (id: string, data: Partial<Product>) =>
    api.put<void>(`/commerce/products/${id}`, data),
  syncToShopify: (id: string) => api.post<void>(`/commerce/products/${id}/sync-shopify`),
  listOrders: () => api.get<Order[]>('/commerce/orders'),
  getOrder: (id: string) => api.get<Order>(`/commerce/orders/${id}`),
  updateOrderStatus: (id: string, body: { status: string; tracking_info?: string; note?: string }) =>
    api.patch<Order>(`/commerce/orders/${id}/status`, body),
  listPayouts: () => api.get<Payout[]>('/commerce/payouts'),
  // Picked-inventory → storefront feed
  harvestAvailable: () => api.get<{ harvest: HarvestAvailable[] }>('/commerce/harvest-available'),
  restock: (body: { variety_id?: string; product_id?: string; name?: string; quantity: number; price?: number }) =>
    api.post<Product>('/commerce/products/restock', body),
  // POD merch (free-plan monetization)
  merchCatalog: () => api.get<{ catalog: PODCatalogItem[] }>('/commerce/merch-catalog'),
  createMerchProduct: (body: { catalog_id: string; name?: string; price?: number; design_image_url?: string }) =>
    api.post<Product>('/commerce/merch-products', body),
}

// ── Shipping & label printing (§55.10) ───────────────────────
export interface ShipAddress {
  name?: string; street1?: string; street2?: string; city?: string
  state?: string; zip?: string; country?: string; phone?: string
}
export interface ShipParcel { weight_oz: number; length_in?: number; width_in?: number; height_in?: number }
export interface ShipRate {
  id: string; carrier: string; service: string; rate: number; currency: string; delivery_days?: number | null
}
export interface ShipRatesResult { shipment_id: string; from: ShipAddress; rates: ShipRate[]; warning?: string }
export interface Shipment {
  id: string; order_id: string; to_name: string; to_city: string; to_state: string; to_zip: string
  carrier: string; service: string; tracking_code: string; label_url: string; label_format: string
  cost: number; created_at: string
}
export const shippingApi = {
  rates: (body: { order_id: string; to: ShipAddress; parcel: ShipParcel; label_format?: string }) =>
    api.post<ShipRatesResult>('/shipping/rates', body),
  buyLabel: (body: { order_id: string; shipment_id: string; rate_id: string; label_format?: string; to: ShipAddress; parcel: ShipParcel }) =>
    api.post<Shipment>('/shipping/label', body),
  forOrder: (orderId: string) => api.get<{ shipments: Shipment[] }>(`/shipping/order/${orderId}`),
}

export interface PODCatalogItem {
  id: string
  sku: string
  name: string
  category: string
  base_cost_usd: number
  suggested_retail_usd: number
  image_url?: string
  vendor: string
}

// Stripe Connect (storefront card payments)
export interface StripeConnectStatus {
  connected: boolean
  account_id?: string
  charges_enabled: boolean
  details_submitted: boolean
  configured: boolean
}
export const paymentsApi = {
  status: () => api.get<StripeConnectStatus>('/payments/status'),
  connect: () => api.post<{ url: string }>('/payments/connect'),
}

// Plant-limit upgrade offer (§242). `eligible` = sustained ≥2 days at the cap
// (auto-show); `at_limit` = capped right now (drives the reactive 402 modal).
export interface UpsellOffer {
  eligible: boolean
  resource: string
  limit: number
  count: number
  at_limit: boolean
  days_at_limit: number
  current_plan: string
  current_plan_name: string
  suggested_plan?: string
  suggested_plan_name?: string
  suggested_limit: number
  price_monthly_usd: number
  price_yearly_usd?: number
  is_founding: boolean
  upgrade_url: string
}
export const billingApi = {
  upsell: () => api.get<UpsellOffer>('/billing/upsell'),
  changePlan: (planId: string) =>
    api.post<{ mode: string; url?: string; error?: string; plan?: string }>('/billing/change-plan', { plan_id: planId }),
}

export interface HarvestAvailable {
  variety_id: string
  variety_name: string
  harvested_kg: number
  listed_qty: number
  suggested_price: number
}

// ── Tenant / Farm profile ────────────────────────────────────
export const tenantApi = {
  get:          ()                                   => api.get<TenantProfile>('/tenant'),
  update:       (data: Partial<TenantProfile>)       => api.put<void>('/tenant', data),
  // Onboarding farm-type/scale/goals — a dedicated partial-safe save (§55.24) that
  // touches only these 3 columns (never blanks the rest of the profile like a partial PUT /tenant).
  saveOnboardingProfile: (data: { farm_type?: string; farm_scale?: string; farm_goals?: string[] }) =>
    api.post<{ status: string }>('/tenant/onboarding-profile', data),
  // Member list/invite/remove are backed by the real RBAC team backend (/team,
  // §185) — the old /tenant/members handlers were empty stubs. Adapt the team
  // shape to TenantMember so the Settings member section renders unchanged.
  members:      ()                                   =>
    teamApi.list().then(r => ({ data: { members: r.data.map(m => ({ id: m.user_id, display_name: m.display_name, email: m.email, roles: m.roles as string[], status: m.status })) as TenantMember[] } })),
  invite:       (email: string, role: string)        => teamApi.invite({ email, roles: [role as TeamRole] }),
  removeMember: (userId: string)                     => teamApi.remove(userId),
  // Public — resolve invite code to farm info (no auth required)
  resolveInvite: (code: string)                      => api.get<{ farm_name: string; logo_url?: string; slug: string }>(`/invites/${code}`),
}

// ── Reports ──────────────────────────────────────────────────
export const reportsApi = {
  yieldOverTime: (params?: { from?: string; to?: string; granularity?: string; variety_ids?: string[] }) =>
    api.get<{ series: DataPoint[]; summary: ChartSummary; primary_unit: string }>('/reports/yield/over-time', { params }),
  yieldYoY: (params?: { compare_year?: number; variety_ids?: string[] }) =>
    api.get<{ months: YoYMonth[]; summary: ChartSummary }>('/reports/yield/yoy', { params }),
  yieldByVariety: (params?: { from?: string; to?: string }) =>
    api.get<{ series: VarietyDataPoint[]; summary: ChartSummary }>('/reports/yield/by-variety', { params }),
  yieldVsWeather: (params?: { from?: string; to?: string; metric?: string }) =>
    api.get<{ yield_series: DataPoint[]; weather_series: DataPoint[]; correlation: number; summary: ChartSummary }>('/reports/yield/vs-weather', { params }),
  expenseVsRevenue: (params?: { from?: string; to?: string }) =>
    api.get<{ series: ExpenseRevenuePoint[]; summary: ChartSummary }>('/reports/expense-vs-revenue', { params }),
  revenueByChannel: (params?: { from?: string; to?: string }) =>
    api.get<RevenueReport>('/reports/revenue-by-channel', { params }),
  cropPerAcre: (params?: { from?: string; to?: string }) =>
    api.get<{ series: AcreYield[]; summary: CropPerAcreSummary }>('/reports/crop-per-acre', { params }),
}

export interface AcreYield {
  block_name: string
  acreage: number
  total_kg: number
  revenue: number
  yield_per_acre: number
  revenue_per_acre: number
  lost_kg: number
  loss_rate_pct: number
}
export interface CropPerAcreSummary {
  total_kg: number
  total_acres: number
  total_revenue: number
  avg_yield_per_acre: number
  unit: string
}

// ── Scheduled reports (recurring CSV exports → GCS + email) ───────────────────
export interface ReportFilters {
  days?: number
  granularity?: string
  compare_year?: number
}
export interface ScheduledReport {
  id: string
  name: string
  report_type: string   // yield_over_time | yield_by_variety | year_over_year | yield_vs_weather | care_summary | expense_vs_revenue | inventory_usage
  format: string        // csv
  schedule: string      // daily | weekly | monthly
  filters: ReportFilters
  destination: string   // gcs | email
  email_to?: string[]
  is_active: boolean
  last_run_at?: string
  last_run_status?: string
  last_run_url?: string
  next_run_at?: string
  created_at?: string
}
export interface ReportRun {
  id: string
  scheduled_report_id: string
  status: string        // running | success | failed
  format: string
  row_count: number
  file_size_bytes: number
  error_message?: string
  started_at: string
  finished_at?: string
}
export const scheduledReportsApi = {
  list: () => api.get<{ reports: ScheduledReport[] }>('/reports/scheduled'),
  create: (body: Partial<ScheduledReport>) => api.post<ScheduledReport>('/reports/scheduled', body),
  update: (id: string, body: Partial<ScheduledReport>) => api.put<ScheduledReport>(`/reports/scheduled/${id}`, body),
  remove: (id: string) => api.delete<void>(`/reports/scheduled/${id}`),
  run: (id: string) => api.post<{ status: string }>(`/reports/scheduled/${id}/run`),
  runs: (id: string) => api.get<{ runs: ReportRun[] }>(`/reports/scheduled/${id}/runs`),
  // Authenticated CSV download → triggers a browser save.
  download: async (id: string, runId: string) => {
    const res = await api.get(`/reports/scheduled/${id}/runs/${runId}/download`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `report_${runId.slice(0, 8)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
}

// Realized sales revenue (from harvest_sales) split by channel + wholesale partner.
export interface ChannelRevenue { channel: string; revenue_cents: number; quantity_kg: number; sale_count: number }
export interface PartnerRevenue { vendor_id: string; vendor_name: string; revenue_cents: number; quantity_kg: number; sale_count: number }
export interface RevenueReport {
  from: string; to: string; currency: string
  total_revenue_cents: number; total_kg: number
  retail_revenue_cents: number; wholesale_revenue_cents: number
  by_channel: ChannelRevenue[]; by_partner: PartnerRevenue[]
}

// ── Superadmin: parcel-source coverage map + health (AdminPortal "Maps" tab) ──
export interface ParcelSource {
  kind: 'state' | 'county'
  code: string   // "FL" or "CA:sonoma" — opaque id for the health check
  name: string
  terms?: string
  lat: number
  lng: number
}
export interface ParcelHealth {
  code: string
  ok: boolean
  http_status: number
  latency_ms: number
  capabilities?: string
  layer_name?: string
  error?: string
}
export interface ParcelJurisdiction {
  code: string
  name: string
  status: 'statewide' | 'statewide+county' | 'county' | 'none'
  statewide_source?: string
  county_count: number
  counties?: string[]
}
export const parcelsAdminApi = {
  sources: () => api.get<{ sources: ParcelSource[] }>('/admin/parcels/sources'),
  health: (code: string) => api.get<ParcelHealth>(`/admin/parcels/health?code=${encodeURIComponent(code)}`),
  coverage: () => api.get<{ jurisdictions: ParcelJurisdiction[]; summary: { statewide: number; county_only: number; none: number; total: number } }>('/admin/parcels/coverage'),
}

// ── Email Templates console (superadmin) ─────────────────────────────────────
// View + override Loops transactional template IDs at runtime (DB override wins
// over the LOOPS_TMPL_* env default) — test a new ID in dev, promote to prod, no
// redeploy — and send a sample test.
export interface EmailTemplate {
  key: string
  label: string
  env_var: string
  vars: string[]
  env_id: string       // Tofu / env-var default
  override_id: string  // admin override ('' = none)
  resolved_id: string  // what actually sends
  source: 'override' | 'env' | 'unset'
}
export const emailTemplatesApi = {
  list: () => api.get<{ templates: EmailTemplate[]; configured: boolean }>('/admin/email-templates'),
  set: (key: string, loops_template_id: string) =>
    api.put<{ templates: EmailTemplate[]; configured: boolean }>(`/admin/email-templates/${encodeURIComponent(key)}`, { loops_template_id }),
  test: (key: string, to?: string) =>
    api.post<{ to: string; configured: boolean }>(`/admin/email-templates/${encodeURIComponent(key)}/test`, { to }),
}

// ── Expenses (hierarchical cost centers + P&L) ───────────────────────────────
export interface CostCenter {
  id: string; parent_id?: string | null; type: string; name: string; code: string
  path: string; description?: string; is_active: boolean
  children?: CostCenter[]; total_ytd?: number
}
export interface Expense {
  id: string; cost_center_id: string; date: string; category: string
  description?: string; vendor_name?: string; amount: number; currency?: string
  tax_amount?: number | null; notes?: string; tags?: string[]
}
export interface Profitability {
  cost_center_id?: string; cost_center_name?: string; period?: string
  revenue: number; expenses: number; gross_profit: number; margin_pct: number
}
export const EXPENSE_CATEGORIES = ['input','labor','equipment','irrigation','propagation','purchase','marketing','overhead','repair','other'] as const
export const COST_CENTER_TYPES = ['farm','category','zone','variety','plant','channel','ops'] as const
export const expensesApi = {
  costCenters: () => api.get<{ cost_centers: CostCenter[] }>('/expenses/cost-centers'),
  createCostCenter: (d: Partial<CostCenter>) => api.post<CostCenter>('/expenses/cost-centers', d),
  deleteCostCenter: (id: string) => api.delete<void>(`/expenses/cost-centers/${id}`),
  list: (params?: { cost_center_id?: string; from?: string; to?: string; category?: string; limit?: number }) =>
    api.get<{ expenses: Expense[] }>('/expenses', { params }),
  log: (d: Partial<Expense>) => api.post<Expense>('/expenses', d),
  remove: (id: string) => api.delete<void>(`/expenses/${id}`),
  farmProfitability: (period?: string) => api.get<Profitability>('/expenses/profitability', { params: { period } }),
}

// ── Audit ────────────────────────────────────────────────────
export const auditApi = {
  // Tenant-scoped: view your own farm's audit trail
  listLogs: (params?: AuditLogParams) =>
    api.get<AuditLogResponse>('/audit/logs', { params }),
  // Superuser: view a specific tenant's audit trail
  listTenantLogs: (tenantId: string, params?: AuditLogParams) =>
    api.get<AuditLogResponse>(`/admin/tenants/${tenantId}/audit-logs`, { params }),
}

// ── User Preferences ─────────────────────────────────────────
export const prefsApi = {
  get: () => api.get<UserPreferences>('/me/preferences'),
  update: (fields: Partial<UserPreferences>) => api.put<void>('/me/preferences', fields),
  getOnboarding: () => api.get<OnboardingProgress>('/me/onboarding'),
  completeStep: (step: OnboardingStep) => api.post<void>(`/me/onboarding/${step}/complete`),
  saveDetails: (body: OnboardingDetails) => api.post<void>('/me/onboarding/details', body),
  dismissOnboarding: () => api.post<void>('/me/onboarding/dismiss'),
  getReleases: () => api.get<{ releases: PlatformRelease[]; last_seen: string }>('/releases'),
  markReleaseSeen: (version: string) => api.put<void>('/me/preferences', { last_seen_release_version: version }),
}

// ── Types (mirrors Go structs) ───────────────────────────────
export interface Tenant { id: string; slug: string; name: string; plan: string }
export interface RegisterPayload { email: string; password: string; name: string; farm_name: string }
export interface Variety {
  id: string; name: string; common_name: string; species: string; category: string
  nursery_source: string; ripening_start_week?: number; ripening_end_week?: number
  average_weight_grams?: number; flavor_profile: string; tags: string[]
  notes: string; attributes: Record<string, unknown>
  purpose?: string // default purpose for plants of this variety; '' = fruit_pick
}
export interface Plant {
  id: string; variety_id?: string; variety?: Variety; label: string
  status: string; planted_at?: string; grafted_at?: string; root_stock: string
  location_feature_id?: string; zone_id?: string; block_id?: string; irrigation_zone_id?: string; height_cm?: number
  canopy_diameter_cm?: number; notes: string; tags: string[]
  purpose?: string // '' | fruit_pick | propagate_for_sale | propagate_to_plant ('' = inherit)
}

// ── Valuation (age-based wholesale/retail price schedules; feature #2) ───────
export interface ValuationPoint { age_months: number; wholesale_usd?: number | null; retail_usd?: number | null }
export interface ValuationSchedule {
  id: string; name: string; item_kind: 'plant' | 'fruit'; variety_id?: string | null
  category: string; currency: string; is_active: boolean; points: ValuationPoint[]; created_at: string
}
export interface ValuationQuote {
  schedule_id?: string | null; schedule_name?: string; age_months: number
  wholesale_usd?: number | null; retail_usd?: number | null; matched: boolean
}
export const valuationApi = {
  list:   () => api.get<{ schedules: ValuationSchedule[] }>('/valuation/schedules'),
  create: (s: Partial<ValuationSchedule>) => api.post<ValuationSchedule>('/valuation/schedules', s),
  update: (id: string, s: Partial<ValuationSchedule>) => api.put<ValuationSchedule>(`/valuation/schedules/${id}`, s),
  remove: (id: string) => api.delete(`/valuation/schedules/${id}`),
  quote:  (p: { kind?: string; variety_id?: string; category?: string; age_months?: number }) =>
    api.get<ValuationQuote>('/valuation/quote', { params: p }),
}
export const PLANT_PURPOSES = [
  { value: '', label: 'Inherit (default)' },
  { value: 'fruit_pick', label: 'Fruit — pick & sell/eat' },
  { value: 'propagate_for_sale', label: 'Propagate — sell the plant' },
  { value: 'propagate_to_plant', label: 'Propagate — plant out' },
] as const
// ── Tags / labels (cross-hierarchy; a tag on a container is inherited by its
// contents; used for filtering + multi-city views) ──────────────────────────
export type TagEntityType = 'location' | 'block' | 'plant_zone' | 'plant' | 'irrigation_zone'
export interface Tag { id: string; name: string; color: string; created_at: string }
export interface TagAssignment { tag_id: string; entity_type: TagEntityType; entity_id: string }
export const tagsApi = {
  list:        () => api.get<{ tags: Tag[] }>('/tags'),
  assignments: () => api.get<{ assignments: TagAssignment[] }>('/tags/assignments'),
  create:      (name: string, color?: string) => api.post<Tag>('/tags', { name, color }),
  remove:      (id: string) => api.delete(`/tags/${id}`),
  // tag_id OR name (name creates/looks-up the tag) + the entity to attach it to.
  assign:      (p: { tag_id?: string; name?: string; color?: string; entity_type: TagEntityType; entity_id: string }) => api.post<Tag>('/tags/assign', p),
  unassign:    (p: { tag_id: string; entity_type: TagEntityType; entity_id: string }) => api.post('/tags/unassign', p),
}
// Pleasant default palette for new tags (cycled by creation order).
export const TAG_COLORS = ['#5B8DEF', '#2D6A2F', '#E5853C', '#A855F7', '#E5533C', '#14b8a6', '#EAB308', '#EC4899']

// Block category — production crops vs ecosystem-support plantings (oak for mulch,
// windbreaks, shade, nitrogen-fixers, pollinator/insectary rows).
export const BLOCK_CATEGORIES = [
  { value: 'production', label: 'Production (fruit/nut crop)' },
  { value: 'support', label: 'Ecosystem support (mulch, shade, windbreak, N-fixer)' },
  { value: 'mixed', label: 'Mixed (crop + support)' },
] as const
export interface CareEvent {
  id: string; plant_id?: string; zone_id?: string; plant_label?: string; zone_name?: string
  action: string; occurred_at: string; notes?: string; cost_usd?: number
  performed_by?: string; due_date?: string; is_overdue?: boolean
}
export interface Observation { id: string; plant_id: string; type: string; notes: string; observed_at: string }
export interface FarmFeature {
  id: string; feature_type: string; name: string; geometry: GeoJSON; properties: Record<string, unknown>
  plant_id?: string; variety_id?: string; location_id?: string | null  // owning farm plot (per-plot boundaries)
}
export interface GeoJSON { type: string; coordinates: unknown }
export interface SatelliteBounds { north: number; south: number; east: number; west: number }
export interface HarvestEvent {
  id: string; plant_id?: string; variety_id?: string; variety_name?: string; block_id?: string; harvested_at: string
  weight_kg?: number; unit_count?: number; grade?: string; brix?: number | null; season?: string; notes: string
}
// A harvest sale/allocation (harvest_sales). channel = 'wholesale' (to a vendor)
// or a retail channel (farmstand/online/facebook/farmers_market/other).
export interface Sale {
  id: string; harvest_id: string
  vendor_id?: string | null; vendor_name?: string | null
  channel: string
  quantity_kg: number; quantity_units?: number | null
  unit_price_cents?: number | null; currency: string
  sale_date: string; invoice_ref?: string | null; notes?: string | null
}
export interface SaleInput {
  vendor_id?: string | null; channel: string
  quantity_kg: number; quantity_units?: number
  unit_price_cents?: number; currency?: string
  sale_date?: string; invoice_ref?: string; notes?: string
}
export interface Vendor {
  id: string; name: string; slug?: string; logo_url?: string | null
  website_url?: string | null; contact_name?: string | null
  contact_email?: string | null; contact_phone?: string | null
  address?: string | null; notes?: string | null; is_active?: boolean
}
export const vendorsApi = {
  list:   () => api.get<{ vendors: Vendor[] }>('/vendors'),
  create: (data: Partial<Vendor>) => api.post<Vendor>('/vendors', data),
  update: (id: string, data: Partial<Vendor>) => api.put<Vendor>(`/vendors/${id}`, data),
  remove: (id: string) => api.delete(`/vendors/${id}`),
}
export interface IrrigationZone { id: string; controller_id: string; station_number: number; name: string; is_active: boolean }
export interface Schedule { id: string; zone_id: string; name: string; type: string; days_of_week: number[]; start_time: string; duration_minutes: number; is_enabled: boolean }
export interface Controller { id: string; type: string; name: string; is_online: boolean; base_url?: string; device_id?: string; zone_count?: number }
export interface PropagationBatch { id: string; name: string; type: string; variety_id?: string; started_at: string; status: string; total_seedlings: number; alive_seedlings: number; success_rate: number }
export interface Seedling { id: string; batch_id: string; label: string; status: string }
export interface GraftRecord { id: string; batch_id: string; graft_method: string; status: string; grafted_at: string; take_at?: string }
export interface SuccessRateSummary { variety_name: string; batch_type: string; total_attempts: number; successes: number; success_rate: number; avg_days_to_take?: number }
export interface WeatherObservation { temp_c?: number; humidity_pct?: number; precip_mm?: number; observed_at: string }
export interface WeatherStation { id: string; provider: string; station_id: string; name: string; is_primary: boolean }
export interface InventoryItem { id: string; name: string; category: string; unit: string; quantity_on_hand: number; reorder_point?: number; npk: string }
export interface InventoryTransaction { type: string; quantity: number; notes: string }
export interface FertilizerSchedule { id: string; name: string; frequency_days: number; application_rate: string; next_due?: string }
export interface Product { id: string; variety_id?: string; name: string; price: number; available_qty: number; is_available: boolean; shopify_product_id?: string; kind?: string; pod_catalog_id?: string; design_image_url?: string; base_cost_usd?: number }
export interface OrderLineItem { product_id: string; name: string; quantity: number; unit_price: number; line_total: number }
export interface Order {
  id: string
  total_amount: number
  created_at: string
  status?: string
  customer_name?: string
  customer_email?: string
  customer_phone?: string
  fulfillment_method?: string
  notes?: string
  source?: string
  items?: OrderLineItem[]
  tracking_info?: string
  admin_note?: string
}
export interface Payout { id: string; amount: number; created_at: string }
export interface VarietySummary { variety_name: string; total_kg: number; total_revenue: number }
export interface SeasonSummary { season: string; total_kg: number; total_revenue: number }
// Reports types
export interface DataPoint { date: string; value: number; label?: string }
export interface VarietyDataPoint { variety_name: string; total_kg: number; total_revenue: number; unit_count?: number }
export interface YoYMonth { month: number; month_name: string; current_kg: number; prior_kg: number; change_pct: number }
export interface ChartSummary { total: number; avg: number; max: number; min: number; change_pct?: number; unit: string }
export interface ExpenseRevenuePoint { month: string; expenses: number; revenue: number; net: number }
export interface TenantProfile {
  id: string; slug: string; name: string; plan: string; status: string
  description?: string; address?: string; city?: string; state?: string
  zip?: string; country?: string; phone?: string; email?: string
  website?: string; timezone?: string; size_acres?: number
  logo_url?: string; established_year?: number
  default_mode?: string; default_theme?: string  // appearance default for new members
  vineyard_mode?: boolean  // vineyard mode (§55.20) — relabels Variety→Vine variety + shows trellis/spacing
}
export interface TenantMember {
  id: string; display_name?: string; email: string; roles: string[]; status: string
}
export interface AuditEntry {
  id: string
  tenant_id: string
  actor_id?: string
  actor_email?: string
  actor_name?: string
  action: string          // 'create' | 'update' | 'delete' | 'login' | 'logout' | 'export' | etc.
  resource_type: string   // 'plant' | 'harvest' | 'variety' | 'vendor' | 'care_event' | 'user' | etc.
  resource_id?: string
  resource_label?: string
  changes?: Record<string, { before: unknown; after: unknown }>
  ip_address?: string
  occurred_at: string
}
export interface AuditLogParams {
  since?: string; until?: string; resource_type?: string; action?: string
  actor_id?: string; limit?: number; cursor?: string
}
export interface AuditLogResponse {
  entries: AuditEntry[]; next_cursor?: string; total?: number
}

// ── User preferences ──────────────────────────────────────────
export interface UserPreferences {
  user_id: string
  theme: 'light' | 'dark' | 'sun'
  measurement_system: 'imperial' | 'metric'
  language: string
  reduce_motion: boolean
  font_size_offset: number       // -1 | 0 | 1 | 2
  high_contrast: boolean
  focus_visible_always: boolean
  screen_reader_verbose: boolean
  last_seen_release_version?: string
  created_at: string
  updated_at: string
}

export type OnboardingStep = 'farm_type' | 'address' | 'first_plant' | 'irrigation' | 'measurement' | 'history' | 'ai' | 'details'

export interface OnboardingProgress {
  tenant_id: string
  step_farm_type: boolean
  step_address: boolean
  step_first_plant: boolean
  step_irrigation: boolean
  step_measurement: boolean
  step_history: boolean
  step_ai: boolean
  step_details: boolean
  all_steps_done: boolean
  dismissed_at?: string
  updated_at: string
}

export interface OnboardingDetails {
  legal_name: string
  farm_name: string
  legal_business_name?: string
  contact_phone?: string
  mailing_same_as_farm: boolean
  mailing_street?: string
  mailing_city?: string
  mailing_state?: string
  mailing_postal?: string
  mailing_country?: string
}

export interface PlatformRelease {
  id: string
  version: string
  title: string
  body_markdown?: string
  summary_bullets: string[]
  is_published: boolean
  published_at?: string
  created_at: string
  updated_at: string
}

// ── Photos (trees, plants, equipment, blocks, harvests, …) ──────────────────
// Real GCS-backed uploads. Reads are proxied through the authenticated API
// (GET /photos/:id streams the image), so the gallery blob-fetches each photo
// with the Bearer token and renders it via an object URL.
export interface Photo {
  id: string
  resource_type: string
  resource_id: string
  caption: string
  is_primary: boolean
  url: string
  created_at: string
}
export const photoApi = {
  list: (resourceType: string, resourceId: string) =>
    api.get<{ photos: Photo[] }>('/photos', { params: { resource_type: resourceType, resource_id: resourceId } }),
  upload: (resourceType: string, resourceId: string, blob: Blob, caption = '') => {
    const fd = new FormData()
    fd.append('file', blob, 'photo.jpg')
    fd.append('resource_type', resourceType)
    fd.append('resource_id', resourceId)
    if (caption) fd.append('caption', caption)
    return api.post<Photo>('/photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  remove: (id: string) => api.delete(`/photos/${id}`),
  setPrimary: (id: string) => api.post(`/photos/${id}/primary`),
  // Authenticated blob fetch → object URL for <img src>.
  fetchObjectURL: async (id: string): Promise<string> => {
    const res = await api.get(`/photos/${id}`, { responseType: 'blob' })
    return URL.createObjectURL(res.data as Blob)
  },
}
